import axios from "axios";
import { API_BASE_URL, API_TIMEOUT } from "../config/api";
import { cacheGet, cacheSet, cacheInvalidateByPrefix } from "./apiCache";

// Create an Axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Include credentials (cookies) in requests
});

// Helper function to add delay for mock API calls
const addDelay = (ms = 2000) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

// Function to send OTP
export const sendOtp = async (countryCode, mobileNumber, role) => {
  try {
    await addDelay(); // Add 2-second delay
    const response = await api.post("/otp/send", {
      countryCode,
      mobileNumber,
      role,
    });
    return response.data;
  } catch (error) {
    console.error("Error sending OTP:", error);
    throw error;
  }
};

// Function to validate OTP and store access token
export const validateOtp = async (countryCode, mobileNumber, otpCode, role) => {
  try {
    await addDelay(); // Add 2-second delay
    const response = await api.post("/otp/validate", {
      countryCode,
      mobileNumber,
      otpCode,
      role,
    });
    localStorage.setItem("accessToken", response.data.accessToken); // Store the access token
    return response.data;
  } catch (error) {
    console.error("Error validating OTP:", error);
    throw error;
  }
};

// Function for trainer login
export const trainerLogin = async (email, password) => {
  try {
    await addDelay(); // Add 2-second delay
    const response = await api.post("/trainer/login", {
      username: email, // Server expects 'username' field
      password,
    });
    localStorage.setItem("accessToken", response.data.accessToken); // Store the access token
    localStorage.setItem("userRole", "trainer"); // Set the user role for trainer
    return response.data;
  } catch (error) {
    console.error("Error trainer login:", error);
    throw error;
  }
};

// Function for admin login
export const adminLogin = async (username, password) => {
  try {
    await addDelay(); // Add 2-second delay
    const response = await api.post("/admin/login", {
      username,
      password,
    });
    localStorage.setItem("accessToken", response.data.accessToken); 
    localStorage.setItem("userRole", "admin"); 
    return response.data;
  } catch (error) {
    console.error("Error admin login:", error);
    throw error;
  }
};

// Function to get trainer profile (cached 15 min)
export const getTrainerProfile = async (trainerId) => {
  const cacheKey = `trainer_profile_${trainerId}`;
  const cached = cacheGet(cacheKey);
  if (cached) {
    console.log('Trainer profile served from cache:', trainerId);
    return cached;
  }
  try {
    console.log('Fetching trainer profile for ID:', trainerId);
    // Get user's timezone for UTC to local conversion
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await api.get(`/trainer/${trainerId}/profile`, {
      params: { timezone }
    });
    console.log('Trainer profile response:', response.data);
    cacheSet(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching trainer profile:", error);
    throw error;
  }
};

// Internal helper: fetch trainer profile WITHOUT using or populating cache.
// Used inside update functions to read current data for preservation
// without re-caching stale data.
const _fetchTrainerProfileNoCache = async (trainerId) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const response = await api.get(`/trainer/${trainerId}/profile`, {
    params: { timezone }
  });
  return response.data;
};

// Function to update trainer profile (invalidates cache for this trainer)
export const updateTrainerProfile = async (profileData) => {
  try {
    console.log('updateTrainerProfile called with:', profileData);
    console.log('profileData keys:', Object.keys(profileData));
    console.log('profileData.trainingPhotos:', profileData.trainingPhotos);
    console.log('profileData.trainingVideos:', profileData.trainingVideos);

    // Fetch current profile to preserve availability data (bypass cache to avoid stale reads)
    const currentProfile = await _fetchTrainerProfileNoCache(profileData._id);
    console.log('Current profile data for preservation:', currentProfile);

    const formData = new FormData();

    // Add all profile fields to FormData
    Object.keys(profileData).forEach(key => {
      if (profileData[key] !== undefined && profileData[key] !== null) {
        if (key === 'trainingPhotos' || key === 'trainingVideos' || key === 'profilePic') {
          // Handle file uploads - only add if we have actual files
          console.log(`Processing file field ${key}:`, profileData[key]);
          console.log(`File field type:`, typeof profileData[key]);
          console.log(`Is File instance:`, profileData[key] instanceof File);
          console.log(`Is Array:`, Array.isArray(profileData[key]));

          if (profileData[key] instanceof File) {
            console.log(`Adding single file ${key}:`, profileData[key].name);
            formData.append(key, profileData[key]);
          } else if (Array.isArray(profileData[key]) && profileData[key].length > 0) {
            console.log(`Adding ${profileData[key].length} files for ${key}`);
            profileData[key].forEach((file, index) => {
              if (file instanceof File) {
                console.log(`Adding file ${index} for ${key}:`, file.name);
                formData.append(key, file);
              } else {
                console.log(`Skipping non-file item ${index} for ${key}:`, file);
              }
            });
          } else {
            console.log(`Skipping empty file field ${key}:`, profileData[key]);
            // Don't add empty file fields to FormData at all
          }
        } else if (key === 'instaLink' || key === 'facebookLink' || key === 'tiktokLink') {
          // Only send social media links if they're not empty
          if (profileData[key] && profileData[key].trim() !== '') {
            formData.append(key, profileData[key]);
          }
        } else {
          // Convert numbers to strings for FormData
          const value = typeof profileData[key] === 'number' ? profileData[key].toString() : profileData[key];
          formData.append(key, value);
        }
      }
    });

    // Always preserve existing availability and exceptions data
    if (currentProfile.availability) {
      formData.append('availability', JSON.stringify(currentProfile.availability));
      console.log('Preserving availability:', currentProfile.availability);
    } else {
      formData.append('availability', JSON.stringify([]));
    }

    if (currentProfile.exceptions) {
      formData.append('exceptions', JSON.stringify(currentProfile.exceptions));
      console.log('Preserving exceptions:', currentProfile.exceptions);
    } else {
      formData.append('exceptions', JSON.stringify([]));
    }

    // Debug: Log FormData contents
    console.log('FormData contents:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }

    console.log('Making request to /trainer/addProfile with FormData');
    const response = await api.post('/trainer/addProfile', formData, {
      headers: {
        'Content-Type': undefined // Remove default JSON content type for FormData
      }
    });

    // Invalidate cache AFTER successful write so next read gets fresh data
    if (profileData._id) {
      cacheInvalidateByPrefix(`trainer_profile_${profileData._id}`);
      cacheInvalidateByPrefix(`trainer_avail_${profileData._id}`);
      cacheInvalidateByPrefix('all_trainers_');
    }

    return response.data;
  } catch (error) {
    console.error("Error updating trainer profile:", error);
    throw error;
  }
};

// Function to get user profile by ID
export const getUserProfile = async (userId) => {
  try {
    const response = await api.get(`/user/getUser/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching user profile:", error);
    throw error;
  }
};

// Function to update the user profile
export const updateProfile = async (profileData) => {
  try {
    const formData = new FormData();

    // Add basic profile fields
    Object.keys(profileData).forEach(key => {
      if (profileData[key] !== undefined && profileData[key] !== null) {
        formData.append(key, profileData[key]);
      }
    });

    const response = await api.post("/user/addProfile", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error updating profile:", error);
    throw error;
  }
};

// Function to refresh the token
export const refreshToken = async () => {
  try {
    console.log("Attempting to refresh token...");
    const response = await axios.post(
      `${API_BASE_URL}/auth/token`,
      {}, // Empty body
      {
        withCredentials: true, // Ensure cookies (like refreshToken) are sent
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const newAccessToken = response.data.accessToken;
    console.log("Token refresh successful:", !!newAccessToken);
    localStorage.setItem("accessToken", newAccessToken); // Update the access token
    setAuthToken(newAccessToken); // Set the new token in axios headers
    return newAccessToken;
  } catch (error) {
    console.error("Error refreshing token:", error);
    throw error;
  }
};

// Function to decode a JWT
export const decodeJwt = (token) => {
  try {
    if (!token || typeof token !== 'string') {
      throw new Error('Invalid token');
    }

    const parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error('Invalid token format');
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('JWT decode error:', error);
    // Return a default structure to prevent crashes
    return {
      exp: 0,
      role: null
    };
  }
};

// Function to check token expiry
export const checkTokenExpiry = (token) => {
  const decodedToken = decodeJwt(token);
  const expiryTime = decodedToken.exp * 1000;
  const currentTime = new Date().getTime();

  return currentTime >= expiryTime;
};

// Axios interceptor to handle token refresh on 403 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const excludedEndpoints = ["/otp/send", "/otp/validate"];

    // Check if the response status is 403 Forbidden and not from excluded endpoints
    if (
      error.response &&
      error.response.status === 403 &&
      !excludedEndpoints.includes(originalRequest.url) &&
      !originalRequest._retry
    ) {
      console.log("403 error detected, attempting token refresh...");
      originalRequest._retry = true;
      try {
        const newAccessToken = await refreshToken();
        // Update the original request's Authorization header
        originalRequest.headers["Authorization"] = `${newAccessToken}`;
        console.log("Retrying original request with new token...");
        // Retry the original request with the new access token
        return api(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh error:", refreshError);
        // If refreshing the token fails, log out the user (clear tokens)
        localStorage.removeItem("accessToken");
        localStorage.removeItem("userRole");
        window.location.href = "/"; // Redirect to role selection page
        throw refreshError;
      }
    }

    return Promise.reject(error);
  }
);

// Function to set Authorization header
export const setAuthToken = (token) => {
  if (token && !checkTokenExpiry(token)) {
    api.defaults.headers.common["Authorization"] = `${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
};

// Set default Authorization header if token is available and valid
const accessToken = localStorage.getItem("accessToken");
if (accessToken && !checkTokenExpiry(accessToken)) {
  setAuthToken(accessToken);
}

// Payment-related functions
export const createPaymentIntent = async (amount, credits) => {
  try {
    console.log('Creating payment intent:', { amount, credits });
    const response = await api.post("/payments/create-intent", {
      amount,
      credits,
    });
    console.log('Payment intent response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating payment intent:", error);
    throw error;
  }
};

export const confirmPayment = async (paymentIntentId, paymentMethodId) => {
  try {
    console.log('Confirming payment:', { paymentIntentId, paymentMethodId });
    const response = await api.post("/payments/confirm", {
      paymentIntentId,
      paymentMethodId,
    });
    console.log('Payment confirmation response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Error confirming payment:", error);
    throw error;
  }
};

export const addCreditsToUser = async (credits) => {
  try {
    console.log('Adding credits to user:', credits);
    const response = await api.post("/user/add-credits", {
      credits,
    });
    console.log('Add credits response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Error adding credits:", error);
    throw error;
  }
};

// Create Stripe checkout session
export const createCheckoutSession = async (credits, userId) => {
  try {
    console.log('Creating checkout session:', { credits, userId });
    const response = await api.post("/user/checkout", {
      credits,
      _id: userId,
    });
    console.log('Checkout session response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Error creating checkout session:", error);
    throw error;
  }
};

export const getCreditHistory = async () => {
  try {
    console.log('Fetching credit history');
    const response = await api.get('/user/credit-history');
    console.log('Credit history response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching credit history:", error);
    throw error;
  }
};

export const checkSlotAvailability = async (trainerId, date, timeSlots) => {
  try {
    console.log('Checking slot availability:', { trainerId, date, timeSlots });
    // Get user's timezone for UTC conversion
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await api.post('/user/check-slot-availability', {
      trainerId,
      date,
      timeSlots,
      timezone
    });
    console.log('Slot availability response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Error checking slot availability:", error);
    throw error;
  }
};

export const cancelSession = async (bookingId) => {
  try {
    if (!bookingId) {
      throw new Error('Booking ID is required');
    }
    const response = await api.post(`/user/cancel-session/${bookingId}`);
    return response.data;
  } catch (error) {
    console.error("Error cancelling session:", error);
    throw error;
  }
};

// Function to get user's session history
export const getUserSessions = async (userId) => {
  try {
    console.log('Fetching user sessions for ID:', userId);
    const response = await api.get(`/user/sessions/${userId}`);
    console.log('User sessions response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching user sessions:", error);
    throw error;
  }
};

// Function to get all trainers with calculated availability slots (cached 15 min)
export const getAllTrainers = async () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const cacheKey = `all_trainers_${timezone}`;
  const cached = cacheGet(cacheKey);
  if (cached) {
    console.log('All trainers served from cache');
    return cached;
  }
  try {
    console.log('Fetching all trainers');
    const response = await api.get(`/trainer/getAllTrainers?timezone=${encodeURIComponent(timezone)}`);
    console.log('All trainers response:', response.data);
    cacheSet(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching trainers:", error);
    throw error;
  }
};

// Function to book a session
export const bookSession = async (bookingData) => {
  try {
    console.log('Booking session with data:', bookingData);
    const response = await api.post('/user/book', bookingData);
    console.log('Booking response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Error booking session:", error);
    throw error;
  }
};

// Function to update trainer availability (invalidates cache)
export const updateTrainerAvailability = async (availabilityData) => {
  try {
    console.log('Updating trainer availability with data:', availabilityData);

    // Fetch current profile to preserve existing data (bypass cache to avoid stale reads)
    const currentProfile = await _fetchTrainerProfileNoCache(availabilityData.trainerId);
    console.log('Current profile data:', currentProfile);

    // The backend expects multipart/form-data with JSON strings for
    // 'availability' and 'exceptions' (same as addProfile format).
    const formData = new FormData();

    // Add trainer ID (required for update)
    if (availabilityData.trainerId) {
      formData.append('_id', availabilityData.trainerId);
    }

    // Preserve all existing profile data (except media fields)
    if (currentProfile.name) formData.append('name', currentProfile.name);
    if (currentProfile.phoneNumber) formData.append('phoneNumber', currentProfile.phoneNumber);
    if (currentProfile.whatsappNumber) formData.append('whatsappNumber', currentProfile.whatsappNumber);
    if (currentProfile.dob) formData.append('dob', currentProfile.dob);
    if (currentProfile.age) formData.append('age', currentProfile.age);
    if (currentProfile.sex) formData.append('sex', currentProfile.sex);
    if (currentProfile.aboutMe) formData.append('aboutMe', currentProfile.aboutMe);
    if (currentProfile.creditsPerHour) formData.append('creditsPerHour', String(currentProfile.creditsPerHour));
    if (currentProfile.instaLink) formData.append('instaLink', currentProfile.instaLink);
    if (currentProfile.facebookLink) formData.append('facebookLink', currentProfile.facebookLink);
    if (currentProfile.tiktokLink) formData.append('tiktokLink', currentProfile.tiktokLink);

    // Add new availability data (times should be in local format - backend will convert to UTC)
    if (availabilityData.availability != null) {
      formData.append('availability', JSON.stringify(availabilityData.availability));
    }
    if (availabilityData.exceptions != null) {
      formData.append('exceptions', JSON.stringify(availabilityData.exceptions));
    }

    // Timezone conversion is now handled in the UI before sending
    // Backend stores UTC times as-is

    // Map field names to backend schema
    if (typeof availabilityData.thresholdPeriod === 'number') {
      formData.append('thresholdPeriod', String(availabilityData.thresholdPeriod));
    }
    if (typeof availabilityData.cooldownPeriod === 'number') {
      formData.append('cooldownPeriod', String(availabilityData.cooldownPeriod));
    }
    if (typeof availabilityData.maxSessionsPerDay === 'number') {
      formData.append('maxSessionsPerDay', String(availabilityData.maxSessionsPerDay));
    }

    const response = await api.post('/trainer/addProfile', formData, {
      headers: {
        'Content-Type': undefined // Remove default JSON content type for FormData
      }
    });
    console.log('Availability update response:', response.data);

    // Invalidate cache AFTER successful write so next read gets fresh data
    if (availabilityData.trainerId) {
      cacheInvalidateByPrefix(`trainer_profile_${availabilityData.trainerId}`);
      cacheInvalidateByPrefix(`trainer_avail_${availabilityData.trainerId}`);
      cacheInvalidateByPrefix('all_trainers_');
    }

    return response.data;
  } catch (error) {
    console.error("Error updating trainer availability:", error);
    throw error;
  }
};

// Function to get trainer profile with availability data (cached 15 min)
export const getTrainerProfileWithAvailability = async (trainerId) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const cacheKey = `trainer_avail_${trainerId}_${timezone}`;
  const cached = cacheGet(cacheKey);
  if (cached) {
    console.log('Trainer profile+availability served from cache:', trainerId);
    return cached;
  }
  try {
    console.log('Fetching trainer profile with availability for ID:', trainerId);
    const response = await api.get(`/trainer/${trainerId}/profile`, {
      params: { timezone }
    });
    console.log('Trainer profile response:', response.data);
    console.log('Trainer availability slots:', response.data?.availabilitySlots);
    cacheSet(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching trainer profile:", error);
    throw error;
  }
};

export const getTrainerBookings = async (trainerId) => {
  try {
    console.log('Fetching bookings for trainer ID:', trainerId);
    const response = await api.get(`/trainer/${trainerId}/bookings`);
    console.log('Trainer bookings response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching trainer bookings:", error);
    throw error;
  }
};

// Address management functions
export const getUserAddresses = async (userId) => {
  try {
    console.log('Fetching addresses for user ID:', userId);
    const response = await api.get(`/user/getUser/${userId}`);
    console.log('User addresses response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching user addresses:", error);
    throw error;
  }
};

export const updateUserAddresses = async (addresses) => {
  try {
    console.log('Updating user addresses:', addresses);
    const response = await api.post('/user/updateAddresses', {
      userId: addresses.userId,
      addresses: addresses.addresses
    });
    console.log('Update addresses response:', response.data);
    return response.data;
  } catch (error) {
    console.error("Error updating addresses:", error);
    throw error;
  }
};

// Function to get trainer earnings breakdown
export const getTrainerEarnings = async (trainerId) => {
  try {
    console.log('Fetching earnings for trainer ID:', trainerId);
    const response = await api.get(`/trainer/${trainerId}/earnings`);
    return response.data;
  } catch (error) {
    console.error('Error fetching trainer earnings:', error);
    throw error;
  }
};

// Fetch credit cost from backend
export const getCreditCost = async () => {
  try {
    const response = await api.get('/payments/credit-cost');
    return response.data;
  } catch (error) {
    console.error("Error fetching credit cost:", error);
    throw error;
  }
};

// Gym functions
export const getAllGyms = async () => {
  try {
    const response = await api.get('/gyms');
    return response.data;
  } catch (error) {
    console.error("Error fetching gyms:", error);
    throw error;
  }
};

export const createGym = async (gymData) => {
  try {
    const response = await api.post('/admin/gym', gymData);
    return response.data;
  } catch (error) {
    console.error("Error creating gym:", error);
    throw error;
  }
};

export default api;
